/**
 * Единый ввод времени по цифрам (макс. 4). Хранение и API — только HH:MM.
 *
 * Коммит: 1→01:00 … 9→09:00; 14→14:00; 930→09:30; 1234→12:34
 * В процессе ввода допускаются только префиксы, которые можно дополнить до валидного времени.
 */

/** HH:MM из ровно 1–4 цифр по правилам коммита; '' для пусто; null если невалидно */
export function commitTimeDigits(digits: string): string | null {
  if (digits.length === 0) return '';
  if (!/^\d+$/.test(digits) || digits.length > 4) return null;

  if (digits.length === 1) {
    const h = parseInt(digits, 10);
    if (h < 0 || h > 9) return null;
    return `${h.toString().padStart(2, '0')}:00`;
  }
  if (digits.length === 2) {
    const hh = parseInt(digits, 10);
    if (hh < 0 || hh > 23) return null;
    return `${hh.toString().padStart(2, '0')}:00`;
  }
  if (digits.length === 3) {
    const h = parseInt(digits[0], 10);
    const mm = parseInt(digits.slice(1), 10);
    if (h < 0 || h > 9 || mm < 0 || mm > 59) return null;
    return `${h.toString().padStart(2, '0')}:${mm.toString().padStart(2, '0')}`;
  }
  const hh = parseInt(digits.slice(0, 2), 10);
  const mm = parseInt(digits.slice(2, 4), 10);
  if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
  return `${hh.toString().padStart(2, '0')}:${mm.toString().padStart(2, '0')}`;
}

/** Существует ли завершение до длины 1…4, дающее валидный commitTimeDigits */
export function isValidTimeDigitPrefix(prefix: string): boolean {
  if (prefix.length === 0) return true;
  if (prefix.length > 4 || !/^\d+$/.test(prefix)) return false;

  for (let L = prefix.length; L <= 4; L++) {
    const pad = L - prefix.length;
    const max = 10 ** pad;
    for (let i = 0; i < max; i++) {
      const suffix = pad === 0 ? '' : i.toString().padStart(pad, '0');
      const t = prefix + suffix;
      if (commitTimeDigits(t) !== null) return true;
    }
  }
  return false;
}

/**
 * Из сырого ввода (в т.ч. с вставкой) — только цифры, макс. 4,
 * обрезка до длиннейшего валидного префикса (недопуск неверного состояния).
 */
export function normalizeTimeDigitsInput(raw: string): string {
  const merged = raw.replace(/\D/g, '').slice(0, 4);
  for (let len = merged.length; len >= 0; len--) {
    const sub = merged.slice(0, len);
    if (isValidTimeDigitPrefix(sub)) return sub;
  }
  return '';
}

/** Отображение при наборе: двоеточие после 1-й цифры при 3–4 символах в режиме H:MM / HH:MM */
export function formatTimeDigitsForDisplay(digits: string): string {
  if (digits.length === 0) return '';
  if (digits.length <= 2) return digits;
  if (digits.length === 3) return `${digits[0]}:${digits.slice(1)}`;
  return `${digits.slice(0, 2)}:${digits.slice(2, 4)}`;
}

export function hhmmToMinutes(hhmm: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!m) return null;
  const h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  if (h < 0 || h > 23 || min < 0 || min > 59) return null;
  return h * 60 + min;
}

/** Если minutes — NaN/∞ (битые данные), не отдаём "NaN:NaN" в UI — подставляем fallback. */
export function minutesToHHMM(minutes: number, fallback = '09:00'): string {
  if (!Number.isFinite(minutes)) return fallback;
  const clamped = Math.max(0, Math.min(24 * 60 - 1, Math.floor(minutes)));
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
}
